import { z } from "zod";
/** Separate read projection: never part of an immutable calculation response or digest. */
const text = z.string().min(1).max(4096);
const identifier = z.string().min(1).max(128);
const digest = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const date = z.string().date();
const sequence = z.number().int().nonnegative().max(1_000_000);
const exactDecimal = z
    .string()
    .max(124)
    .regex(/^-?(0|[1-9]\d{0,40})(\.\d{1,80})?$/);
const unit = z.enum(["BRL", "RATE", "COUNT"]);
const httpsHref = z
    .string()
    .max(4096)
    .url()
    .regex(/^https:\/\/[^/@\s]+(?:[/?#][^\s]*)?$/i);
// Only these internal destinations are admitted; no relative/protocol-relative navigation.
const ruleHref = z
    .string()
    .max(2048)
    .regex(/^\/rules\/(?!\.{1,2}\?)(?!%2[eE](?:%2[eE])?\?)(?:[A-Za-z0-9_!~*'()-]|\.(?=[A-Za-z0-9_-])|%[a-fA-F0-9]{2}){1,512}\?rulesetVersion=(?:[A-Za-z0-9_.!~*'()-]|%[a-fA-F0-9]{2}){1,512}$/);
const evidenceHref = z
    .string()
    .max(1024)
    .regex(/^\/evidence\/[A-Za-z0-9_-]{1,128}$/);
const rounding = z
    .object({
    scale: z.number().int().min(0).max(80).nullable(),
    mode: z.literal("HALF_UP").nullable(),
    stage: text,
})
    .strict();
export const calculationMemoryCardSchema = z
    .object({
    id: identifier,
    status: z.enum(["CALCULATED", "DECLARED", "EXEMPT", "OUTSIDE_SCOPE", "PENDING"]),
    formula: text,
    substitution: text.nullable(),
    rawResult: exactDecimal.nullable(),
    result: exactDecimal.nullable(),
    storedResult: exactDecimal.nullable(),
    unit,
    dependencies: z.array(identifier).max(64),
    operands: z.array(z.object({ name: identifier, value: exactDecimal, unit }).strict()).max(64),
    rounding,
    source: z
        .object({
        kind: z.enum(["INPUT", "PINNED_RULE", "STORED_TRACE", "DECLARED_REGIME"]),
        ruleId: identifier.nullable(),
        ruleHref: ruleHref.nullable(),
        traceSequences: z.array(sequence).max(256),
        legalReference: text.nullable(),
    })
        .strict(),
})
    .strict();
export const calculationMemorySchema = z
    .object({
    version: z.literal("CALCULATION_MEMORY_V1"),
    status: z.enum(["AVAILABLE", "PARTIAL", "UNAVAILABLE"]),
    legalQualification: z.literal("NOT_PERFORMED"),
    bindings: z
        .object({
        calculationId: z.string().uuid(),
        itemId: identifier,
        position: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable(),
        calculationDate: date,
        requestHash: digest,
        requestHashKind: z
            .enum(["CANONICAL_BATCH", "ATTRIBUTABLE_INTEGRATION_SEMANTICS"])
            .nullable(),
        responseDigest: digest,
        rulesetId: z.string().uuid(),
        rulesetVersion: identifier,
        rulesetContentDigest: digest,
        engineVersion: identifier,
        policyVersion: identifier,
    })
        .strict(),
    quantityBasis: z
        .object({
        sourceType: z.enum(["UNIT", "LINE_TOTAL", "LEGACY_UNSPECIFIED"]),
        amountScope: z.enum(["LINE_TOTAL", "LEGACY_UNIT"]),
        commercialUnit: identifier.nullable(),
        quantity: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
        originalBaseCost: exactDecimal,
        extensionMultiplier: exactDecimal,
        normalizedBase: exactDecimal,
        resultAggregationMultiplier: exactDecimal,
        normalization: z.enum([
            "EXTEND_THEN_ROUND_BEFORE_TAX",
            "ROUND_LINE_BEFORE_TAX",
            "LEGACY_NO_LINE_NORMALIZATION",
        ]),
    })
        .strict()
        .nullable(),
    cards: z.array(calculationMemoryCardSchema).max(64),
    st: z
        .object({
        originalMva: exactDecimal,
        appliedMva: exactDecimal,
        unroundedAdjustmentCandidate: exactDecimal.nullable(),
        displayedMva: exactDecimal,
        mvaSource: z.enum([
            "MATERIALIZED_RULE",
            "LEGACY_NOMINAL_DERIVATION",
            "EFFECTIVE_BURDEN_POLICY",
        ]),
        adjustmentIntraRate: exactDecimal,
        effectiveInternalRateSource: z.enum([
            "LEGACY_NOMINAL",
            "RULE",
            "NOMINAL_NOT_INFORMED",
            "NOMINAL_OUT_OF_VALIDITY",
        ]),
        nominalDebitRate: exactDecimal,
        interstateRate: exactDecimal,
        adjustmentReason: text,
        burdenBasis: z
            .object({
            legalReference: text,
            sourceUrl: httpsHref.nullable(),
            validFrom: date.nullable(),
            validTo: date.nullable(),
        })
            .strict()
            .nullable(),
        burdenBasisUsed: z.boolean(),
        warning: text.nullable(),
        stSeed: exactDecimal,
        reductionFactor: exactDecimal,
        stBase: exactDecimal,
        grossDebit: exactDecimal,
        ownIcmsRate: exactDecimal,
        ownIcmsRaw: exactDecimal,
        ownIcmsRounded: exactDecimal,
        ownIcmsDeductionStage: z.literal("RAW_BEFORE_ST_ROUNDING"),
        netStBeforeFloor: exactDecimal,
        netStRaw: exactDecimal,
        netStRounded: exactDecimal,
        moneyRounding: z
            .object({
            scale: z.literal(2),
            mode: z.literal("HALF_UP"),
            stage: z.literal("AFTER_GROSS_MINUS_RAW_OWN_AND_ZERO_FLOOR"),
        })
            .strict(),
        mvaRounding: z
            .object({
            scale: z.literal(4).nullable(),
            mode: z.literal("HALF_UP").nullable(),
            displayScale: z.union([z.literal(4), z.literal(8)]),
        })
            .strict(),
    })
        .strict()
        .nullable(),
    blockers: z
        .array(z.object({ code: identifier, detail: text, traceSequence: sequence.nullable() }).strict())
        .max(256),
    provenance: z
        .array(z
        .object({
        ruleId: identifier,
        ruleHref,
        rulesetVersion: identifier,
        evidenceId: identifier,
        evidenceHref,
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
        legalReference: text.nullable(),
        officialHref: httpsHref.nullable(),
        validFrom: date.nullable(),
        validTo: date.nullable(),
        authority: z.literal("PINNED_RECORD_ONLY"),
    })
        .strict())
        .max(256),
})
    .strict();
//# sourceMappingURL=calculation-memory.js.map